/**
 * ClusterLabel — Editable cluster name above its background rectangle.
 *
 * Phase 4.3 visual: Geist Mono uppercase, muted-foreground by default,
 * card-tint + foreground on hover. Click to enter inline edit mode; Enter
 * saves, Escape reverts. Sienna border on the input matches the brand spec.
 *
 * Position offset matches the prototype: 20px left of bounds, 40px above —
 * keeps the label clear of the cluster rectangle's rounded corner.
 */

import { useEffect, useRef, useState } from 'react';

interface ClusterLabelProps {
  clusterId: string;
  name: string;
  x: number;
  y: number;
  onRename: (clusterId: string, newName: string) => void;
}

export function ClusterLabel({ clusterId, name, x, y, onRename }: ClusterLabelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  function handleSave() {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== name) {
      onRename(clusterId, trimmed);
    } else {
      setEditValue(name);
    }
    setIsEditing(false);
  }

  // Sync local edit value if the canonical name changes (e.g., after a remote rename)
  useEffect(() => {
    if (!isEditing) setEditValue(name);
  }, [name, isEditing]);

  return (
    <div className="absolute z-10" style={{ left: x - 20, top: y - 40 }}>
      {isEditing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') {
              setEditValue(name);
              setIsEditing(false);
            }
          }}
          className="kv-eyebrow outline-none"
          style={{
            width: 160,
            padding: '1px 5px',
            border: '1px solid hsl(var(--sienna))',
            borderRadius: 4,
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="kv-eyebrow transition-colors duration-[120ms] hover:[background:hsl(var(--card))] hover:[color:hsl(var(--foreground))]"
          style={{
            padding: '2px 6px',
            borderRadius: 4,
            cursor: 'text',
            color: 'hsl(var(--muted-foreground))',
            fontWeight: 600,
            lineHeight: 1.4,
          }}
          title="Click to rename"
        >
          {name}
        </button>
      )}
    </div>
  );
}
